
import { Vue, Component } from "vue-property-decorator";
import { IPaperlessExtractionLog, ICrnLookupResult } from "@/store/types";
import notifications from "@/lib/notifications";
import { rawApi } from "@/lib/api";
import moment from "moment";
import { getModule } from "vuex-module-decorators";
import BatchIndexModule from "@/store/modules/batchIndex";

@Component({
    components: {}
})
export default class PaperlessBatchIndexing extends Vue {
    batchIndexModule = getModule(BatchIndexModule);
    record: IPaperlessExtractionLog | null = null;
    orderNumber: string = "";
    isDeleted: boolean = false;
    saving: boolean = false;
    pdfUrl: string = "";
    documentType: string = "POD";
    
    // Lookup modal properties
    showLookupModal: boolean = false;
    crnLookup: string = "";
    billToLookup: string = "";
    manufacturerLookup: string = "";
    lookupResults: ICrnLookupResult[] = [];
    crnLookupRunning: boolean = false;

    $refs!: {
        orderNumber: HTMLTextAreaElement;
    };

    get documentTypes() {
        return [ 
            {text: "Bill of Lading", value: "BOL"},
            {text: "Packing Slip", value: "PS"}
        ];
    }

    async created() {
        await this.loadRecord();
    }

    beforeDestroy() {
        // Clean up blob URL to prevent memory leak
        if (this.pdfUrl && this.pdfUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.pdfUrl);
        }
    }

    async loadRecord() {
        try {
            const workItemId = parseInt(this.$route.params.workItemId);
            const result = await rawApi.get(`/paperless/${workItemId}`);
            
            if (result.data) {
                // The API returns { log: {...}, pdfData: "base64string", contentType: "application/pdf", ... }
                this.record = result.data.log;
                this.isDeleted = result.data.log.isDeleted || false;
                
                // Convert base64 PDF data to blob URL for display
                if (result.data.pdfData) {
                    const byteCharacters = atob(result.data.pdfData);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: result.data.contentType || 'application/pdf' });
                    this.pdfUrl = URL.createObjectURL(blob);
                }
            }
        } catch (ex) {
            notifications.error("Loading Failed", "Unable to load paperless document.", ex);
            console.error(ex);
            this.navigateToList();
        }
    }

    async saveChanges() {
        if (!this.record) return;

        this.saving = true;
        if(this.orderNumber == null || this.orderNumber.trim() === "") {          
            try {
                await rawApi.put(`/paperless/${this.record.id}`, {
                    isDeleted: this.isDeleted
                });
                
                notifications.success("Saved", "Changes saved successfully.");
                this.navigateToList();
            } catch (ex) {
                notifications.error("Save Failed", "Unable to save changes.", ex);
            } finally {
                this.saving = false;
            }
        }
        else {
            try {
                await rawApi.post(`/paperless/${this.record.id}/submit`, {
                    orderNumber: this.orderNumber,
                    documentType: this.documentType
                });
                
                notifications.success("Submitted", "Document submitted successfully.");
                this.navigateToList();
            } catch (ex) {
                notifications.error("Submit Failed", "Unable to submit document.", ex);
                this.saving = false;
            }
        }
    }

    goBack() {
        this.navigateToList();
    }

    navigateToList() {
        // Preserve the status filter when navigating back
        const query = this.$route.query.status ? { status: this.$route.query.status } : {};
        this.$router.push({ path: "/paperless/batch", query });
    }

    formatDate(date: Date | string): string {
        if (!date) return "";
        return moment(date).format("MM/DD/YYYY hh:mm A");
    }

    getOrderNumbers(orderNumberString: string): string[] {
        if (!orderNumberString) return [];
        return orderNumberString.split(',').map(num => num.trim()).filter(num => num.length > 0);
    }

    onOrderNumberChange() {
        // Handle order number change if needed
    }

    promptLookupDialog() {
        this.crnLookup = "";
        this.lookupResults = [];
        this.showLookupModal = true;
    }

    onlyUnique(value, index, array) {
       return array.indexOf(value) === index;
    }

    lookupAccept() {
        var newOrders = this.lookupResults.filter(x => x.isSelected).map(x => x.orderNumber);
        this.orderNumber = this.orderNumber.split(' ').concat(newOrders).filter(this.onlyUnique).join(' ');
        this.showLookupModal = false;
    }

    lookupCancel() {
        this.showLookupModal = false;
    }

    setFieldFocus() {
        this.$nextTick(() => {
            this.$refs.orderNumber.focus();
        });
    }

    getLookupRowClass(result: ICrnLookupResult): string {
        return result.isSelected ? "selected" : "";
    }

    onLookupRowClicked(result: ICrnLookupResult) {
        result.isSelected = !result.isSelected;
    }

    async performCrnLookup() {
        if (!this.billToLookup?.trim() && !this.manufacturerLookup?.trim()) {
            notifications.display("Required", "Please include the Bill To and/or Manufacturer to find orders.", "warning");
            return;
        }

        if (!this.crnLookup?.trim()) {
            notifications.display("Required", "Please include the CRN to find orders.", "warning");
            return;
        }

        this.crnLookupRunning = true;

        const payload = {
            crn: this.crnLookup,
            billTo: this.billToLookup,
            manufacturer: this.manufacturerLookup,
            facility: "" // Paperless doesn't have facility context
        };
    
        try{
            this.lookupResults = await this.batchIndexModule.getCrnLookup(payload);
        } catch (ex) {
            notifications.error("CRN Lookup Failed", "Unable to perform order lookup by CRN.");
        } finally {
            this.crnLookupRunning = false;
        }
    }

    async saveApproved() {
        if (!this.record) return;

        this.saving = true;
        try {
            await rawApi.put(`paperless/${this.record.id}/approve`);
            notifications.success("Approved", "Document approved successfully.");
            this.navigateToList();
        } catch (ex) {
            notifications.error("Approve Failed", "Unable to approve document.", ex);
            this.saving = false;
        }
    }

    async saveFailed() {
        if (!this.record) return;

        this.saving = true;
        try {
            await rawApi.put(`paperless/${this.record.id}/failed`);
            notifications.success("Marked as Failed", "Document marked as failed.");
            this.navigateToList();
        } catch (ex) {
            notifications.error("Failed Status Update", "Unable to mark document as failed.", ex);
            this.saving = false;
        }
    }
}
